import { useState } from 'react';
import {
  LogoEU, LogoDoS, LogoUNDP, LogoASEAN,
  LogoFord, LogoLuce, LogoCarnegie, LogoGreenpeace,
  LogoTNC, LogoGlobethics, LogoUGM, LogoWCC,
} from '../components/PartnerLogos';
import useScrollReveal from '../hooks/useScrollReveal';
import './Partnerships.css';

const partners = [
  {
    id: 1, Logo: LogoEU,
    name: 'European Union',
    category: 'bilateral',
    website: 'https://europa.eu',
  },
  {
    id: 2, Logo: LogoDoS,
    name: 'US Department of State',
    category: 'bilateral',
    website: 'https://state.gov',
  },
  {
    id: 3, Logo: LogoUNDP,
    name: 'UNDP',
    fullName: 'United Nations Development Programme',
    category: 'bilateral',
    website: 'https://undp.org',
  },
  {
    id: 4, Logo: LogoASEAN,
    name: 'ASEAN Secretariat',
    category: 'bilateral',
    website: 'https://asean.org',
  },
  {
    id: 5, Logo: LogoFord,
    name: 'Ford Foundation',
    category: 'foundation',
    website: 'https://fordfoundation.org',
  },
  {
    id: 6, Logo: LogoLuce,
    name: 'Henry Luce Foundation',
    category: 'foundation',
    website: 'https://hluce.org',
  },
  {
    id: 7, Logo: LogoCarnegie,
    name: 'Carnegie Council',
    fullName: 'Carnegie Council for Ethics in International Affairs',
    category: 'foundation',
    website: 'https://carnegiecouncil.org',
  },
  {
    id: 8, Logo: LogoGreenpeace,
    name: 'Greenpeace',
    category: 'ngo',
    website: 'https://greenpeace.org',
  },
  {
    id: 9, Logo: LogoTNC,
    name: 'The Nature Conservancy',
    category: 'ngo',
    website: 'https://nature.org',
  },
  {
    id: 10, Logo: LogoGlobethics,
    name: 'Globethics',
    fullName: 'Globethics — International Ethics Network',
    category: 'academic',
    website: 'https://globethics.net',
  },
  {
    id: 11, Logo: LogoUGM,
    name: 'Universitas Gadjah Mada',
    category: 'academic',
    website: 'https://ugm.ac.id',
  },
  {
    id: 12, Logo: LogoWCC,
    name: 'World Council of Churches',
    category: 'academic',
    website: 'https://oikoumene.org',
  },
];

const cats = [
  { id: 'all',        label: 'All Partners',              count: partners.length },
  { id: 'bilateral',  label: 'Bilateral & Multilateral',  count: 4 },
  { id: 'foundation', label: 'Foundations',               count: 3 },
  { id: 'ngo',        label: 'NGOs & Environment',        count: 2 },
  { id: 'academic',   label: 'Academic & Institutional',  count: 3 },
];

export default function Partnerships() {
  useScrollReveal();
  const [active, setActive] = useState('all');
  const filtered = active === 'all' ? partners : partners.filter(p => p.category === active);

  return (
    <main>
      {/* ── Hero ────────────────────────────────────────── */}
      <section className="page-hero" aria-labelledby="partnerships-heading">
        <div className="container reveal reveal-up">
          <span className="section-label">Global Network</span>
          <h1 id="partnerships-heading">Partner Institutions &amp; Funders</h1>
          <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.72)' }}>
            A two-decade network of bilateral agencies, international foundations, NGOs, and academic institutions that have shaped and supported Dr. Sofjan's research, advocacy, and international engagement.
          </p>
        </div>
      </section>

      {/* ── Summary strip ───────────────────────────────── */}
      <div className="partners__summary-strip reveal reveal-up">
        <div className="container">
          {[
            { n: '12+', l: 'Partner Institutions' },
            { n: '4',   l: 'Bilateral / Multilateral' },
            { n: '3',   l: 'Major Foundations' },
            { n: '20+', l: 'Years of Partnership' },
          ].map(s => (
            <div key={s.l} className="partners__summary-item">
              <span className="partners__summary-num">{s.n}</span>
              <span className="partners__summary-lbl">{s.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Partners ────────────────────────────────────── */}
      <section className="section">
        <div className="container">

          {/* Filter tabs */}
          <div className="partners__tabs" role="tablist" aria-label="Filter by partner type">
            {cats.map(cat => (
              <button
                key={cat.id}
                role="tab"
                aria-selected={active === cat.id}
                id={`partner-tab-${cat.id}`}
                className={`partners__tab ${active === cat.id ? 'partners__tab--active' : ''}`}
                onClick={() => setActive(cat.id)}
              >
                {cat.label}
                <span className="partners__tab-count">{cat.count}</span>
              </button>
            ))}
          </div>

          {/* Partner grid */}
          <div className="partners__grid">
            {filtered.map((partner, i) => {
              const LogoComponent = partner.Logo;
              return (
                <a
                  key={partner.id}
                  id={`partner-${partner.id}`}
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`partner-card reveal reveal-scale delay-${(i % 4) + 1}`}
                  aria-label={`Visit ${partner.name}`}
                >
                  {/* Logo */}
                  <div className="partner-card__logo-area">
                    <LogoComponent size={68} />
                  </div>

                  {/* Info */}
                  <div className="partner-card__info">
                    <div className="partner-card__header">
                      <h3 className="partner-card__name">{partner.name}</h3>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="partner-card__ext">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8.5 2.5H13.5V7.5M13.5 2.5L6 10M3 4H1V15H12V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                    </svg>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Engagement note ─────────────────────────────── */}
      <section className="section section-alt">
        <div className="container-narrow text-center">
          <span className="section-label">Collaborate</span>
          <h2 className="section-title">Interested in Partnership?</h2>
          <p style={{ marginBottom: '2rem', color: 'var(--text-mid)', fontSize: '1.02rem', lineHeight: 1.75 }}>
            Dr. Sofjan actively welcomes research collaborations, speaking invitations, grant partnerships, and institutional cooperation — particularly in the areas of Islamic ethics, interfaith environmental dialogue, and sustainable development.
          </p>
          <a href="/contact" className="btn btn-primary">Send a Partnership Enquiry</a>
        </div>
      </section>
    </main>
  );
}
